import { useState } from "react";
import { AlertTriangle, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { spaceOf } from "@/lib/spaces";
import { aiOn, useSettings } from "@/context/SettingsContext";
import { useApp } from "@/context/AppContext";
import { VoiceButton } from "@/components/ai/VoiceButton";

export const DuplicateHint = ({ items }) => {
  const { openTicket } = useApp();
  if (!items?.length) return null;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100" data-testid="duplicate-warning">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" />
      <div>Looks like something already open:
        {items.map((d) => <button key={d.id} type="button" onClick={() => openTicket(d.id)} className="ml-1 font-semibold underline-offset-2 hover:underline" data-testid={`duplicate-${d.id}`}>"{d.title}" ({spaceOf(d.space).name})</button>)}
      </div>
    </div>
  );
};

export const QuickFill = ({ onFill }) => {
  const { team } = useSettings();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState("");
  if (!aiOn(team, "quick_add")) return null;
  const go = async (t = text) => {
    if (t.trim().length < 2) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/parse-reminder", { text: t }, { timeout: 60000 });
      onFill(data);
      setWhy(data.priority_reason || "");
      toast.success("Filled in. Check it over and hit Set reminder.");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-2 rounded-xl border border-cyan-400/25 bg-cyan-400/[0.06] p-3" data-testid="ai-quick-fill">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-cyan-300"><Sparkles className="h-3.5 w-3.5" /> Just say it</p>
      <div className="flex items-center gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); go(); } }}
          placeholder="e.g. chase the Openreach order for Smith Ltd tomorrow 9am, urgent" data-testid="ai-quick-fill-input"
          className="h-9 min-w-0 flex-1 rounded-full border bg-background/60 px-4 text-sm outline-none focus:border-cyan-400/60" />
        {aiOn(team, "voice") && <VoiceButton onText={(t) => { setText(t); go(t); }} />}
        <button type="button" onClick={() => go()} disabled={busy} data-testid="ai-quick-fill-btn" className="flex h-9 items-center gap-1.5 rounded-full bg-cyan-400 px-4 text-sm font-semibold text-black transition-colors hover:bg-cyan-300 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Fill in
        </button>
      </div>
      {why && <p className="text-xs text-muted-foreground" data-testid="ai-priority-reason">Priority: {why}</p>}
    </div>
  );
};
